import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';

import {
  DEMO_JUKEBOX_TRACKS,
  JUKEBOX_BUMP_COINS,
  formatTrackDuration,
  type JukeboxQueueItem,
  type JukeboxTrack,
} from './jukebox-demo-data.js';
import './JukeboxPanel.css';

export interface JukeboxMenuBarProps {
  showId: string | null;
  modelUsername: string;
  canTip: boolean;
  coinBalance: number;
  isTipping: boolean;
  viewerUsername: string | null;
  onBalanceChange: (total: number) => void;
  playerEl: HTMLElement | null;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
}

export function JukeboxMenuBar({
  showId,
  modelUsername,
  canTip,
  coinBalance,
  isTipping,
  viewerUsername,
  onBalanceChange,
  playerEl,
  menuOpen,
  onMenuOpenChange,
}: JukeboxMenuBarProps): ReactElement {
  const { requireAuth } = useAuthModal();
  const { requireCoins } = useCoinShop();
  const { api } = useAuth();
  const [query, setQuery] = useState('');
  const [nowPlaying, setNowPlaying] = useState<JukeboxQueueItem | null>(null);
  const [queue, setQueue] = useState<JukeboxQueueItem[]>([]);
  const [progress, setProgress] = useState(0);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [busyTrackId, setBusyTrackId] = useState<string | null>(null);
  const [pendingInteractionIds, setPendingInteractionIds] = useState<string[]>([]);
  const [catalogTracks, setCatalogTracks] = useState<readonly JukeboxTrack[]>(
    import.meta.env.DEV ? DEMO_JUKEBOX_TRACKS : [],
  );
  const [bumpCoins, setBumpCoins] = useState(JUKEBOX_BUMP_COINS);
  const [jukeboxEnabled, setJukeboxEnabled] = useState(false);

  const loadJukebox = useCallback(async (): Promise<void> => {
    try {
      const result = await api.getModelJukebox(modelUsername);
      setJukeboxEnabled(result.settings.enabled);
      setBumpCoins(result.settings.bumpCoins);
      const enabledTracks = result.tracks.filter((track) => track.enabled);
      if (enabledTracks.length > 0) {
        setCatalogTracks(
          enabledTracks.map((track) => ({
            id: track.id,
            title: track.title,
            artist: track.artist,
            durationSec: track.durationSec,
            requestCoins: result.settings.requestCoins,
          })),
        );
      } else {
        setCatalogTracks(import.meta.env.DEV ? DEMO_JUKEBOX_TRACKS : []);
      }
    } catch {
      // Catalog API unavailable — keep the button off rather than fake a live song.
      setJukeboxEnabled(false);
      setBumpCoins(JUKEBOX_BUMP_COINS);
      setCatalogTracks(import.meta.env.DEV ? DEMO_JUKEBOX_TRACKS : []);
    }
  }, [api, modelUsername]);

  const syncNowPlaying = useCallback(async (): Promise<void> => {
    if (!showId) {
      setNowPlaying(null);
      setProgress(0);
      return;
    }
    try {
      const result = await api.getJukeboxNowPlaying(showId);
      const playing = result.nowPlaying;
      if (!playing) {
        setNowPlaying(null);
        setProgress(0);
        return;
      }
      const started = Date.parse(playing.startedAt);
      const elapsedSec = Number.isFinite(started)
        ? Math.max(0, (Date.now() - started) / 1000)
        : playing.progress * playing.durationSec;
      const nextProgress = Math.min(1, elapsedSec / Math.max(1, playing.durationSec));
      setProgress(nextProgress);
      setNowPlaying({
        queueId: playing.interactionId ?? playing.trackId ?? 'now',
        track: {
          id: playing.trackId ?? 'now',
          title: playing.title,
          artist: playing.artist,
          durationSec: playing.durationSec,
          requestCoins: 0,
        },
        requestedBy: playing.requestedBy,
        bumpCoins,
      });
      if (playing.interactionId) {
        setQueue((current) => current.filter((item) => item.queueId !== playing.interactionId));
      }
    } catch {
      // Keep last known state on transient poll errors.
    }
  }, [api, bumpCoins, showId]);

  useEffect(() => {
    setNowPlaying(null);
    setQueue([]);
    setProgress(0);
    setStatusNote(null);
    setQuery('');
    void loadJukebox();
  }, [loadJukebox]);

  useEffect(() => {
    if (!showId || !jukeboxEnabled) {
      return;
    }
    void syncNowPlaying();
    const intervalId = window.setInterval(() => {
      void syncNowPlaying();
    }, 2_000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [jukeboxEnabled, showId, syncNowPlaying]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onMenuOpenChange(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, onMenuOpenChange]);

  useEffect(() => {
    if (!nowPlaying) {
      return;
    }
    const duration = Math.max(1, nowPlaying.track.durationSec);
    const timer = window.setInterval(() => {
      setProgress((current) => Math.min(1, current + 1 / duration));
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [nowPlaying]);

  useEffect(() => {
    if (!showId || pendingInteractionIds.length === 0) {
      return;
    }

    let cancelled = false;
    const poll = (): void => {
      void Promise.all(
        pendingInteractionIds.map((interactionId) =>
          api.getShowInteraction(showId, interactionId).then((result) => result.interaction),
        ),
      )
        .then(async (rows) => {
          if (cancelled) {
            return;
          }
          const stillWaiting: string[] = [];
          let acceptedLabel: string | null = null;
          let declined = false;
          for (const row of rows) {
            if (row.status === 'WAITING') {
              stillWaiting.push(row.id);
            } else if (row.status === 'ACCEPTED') {
              acceptedLabel = row.label;
            } else if (row.status === 'DECLINED') {
              declined = true;
              setQueue((current) => current.filter((item) => item.queueId !== row.id));
            }
          }
          setPendingInteractionIds(stillWaiting);
          if (acceptedLabel) {
            setStatusNote(
              t('common.show.jukeboxAccepted').replace('{{title}}', acceptedLabel),
            );
            void syncNowPlaying();
          } else if (declined) {
            setStatusNote(t('common.show.jukeboxDeclined'));
            const wallet = await api.getSpendWallet();
            onBalanceChange(wallet.total);
          }
        })
        .catch(() => undefined);
    };

    poll();
    const intervalId = window.setInterval(poll, 2_500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [api, onBalanceChange, pendingInteractionIds, showId, syncNowPlaying]);

  const catalog = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return [...catalogTracks];
    }
    return catalogTracks.filter(
      (track) =>
        track.title.toLowerCase().includes(needle) ||
        track.artist.toLowerCase().includes(needle),
    );
  }, [query, catalogTracks]);

  const requestTrack = (track: JukeboxTrack): void => {
    requireAuth(() => {
      if (!showId || !canTip || isTipping) {
        return;
      }
      if (!requireCoins(coinBalance, track.requestCoins, () => {
        requestTrack(track);
      })) {
        return;
      }
      setBusyTrackId(track.id);
      setStatusNote(null);
      void api
        .createShowInteraction(showId, {
          kind: 'JUKEBOX',
          label: `${track.title} · ${track.artist}`,
          amountCoins: track.requestCoins,
          jukeboxTrackId: track.id,
        })
        .then(async (result) => {
          const wallet = await api.getSpendWallet();
          onBalanceChange(wallet.total);
          const entry: JukeboxQueueItem = {
            queueId: result.interaction.id,
            track,
            requestedBy: viewerUsername ?? 'you',
            bumpCoins,
          };
          setQueue((current) => [...current, entry]);
          setPendingInteractionIds((current) => [...current, result.interaction.id]);
          setStatusNote(t('common.show.jukeboxQueued').replace('{{title}}', track.title));
        })
        .catch((err: unknown) => {
          setStatusNote(
            err instanceof Error ? err.message : t('common.show.jukeboxNeedCoins'),
          );
        })
        .finally(() => {
          setBusyTrackId(null);
        });
    }, 'jukebox');
  };

  const bumpItem = (item: JukeboxQueueItem): void => {
    requireAuth(() => {
      if (!showId || !canTip || isTipping) {
        return;
      }
      if (!requireCoins(coinBalance, item.bumpCoins, () => {
        bumpItem(item);
      })) {
        return;
      }
      setBusyTrackId(item.queueId);
      void api
        .createShowInteraction(showId, {
          kind: 'JUKEBOX',
          label: `Bump · ${item.track.title}`,
          amountCoins: item.bumpCoins,
          jukeboxTrackId: item.track.id,
        })
        .then(async () => {
          const wallet = await api.getSpendWallet();
          onBalanceChange(wallet.total);
          setQueue((current) => {
            const without = current.filter((row) => row.queueId !== item.queueId);
            return [item, ...without];
          });
          setStatusNote(t('common.show.jukeboxBumped').replace('{{title}}', item.track.title));
        })
        .catch((err: unknown) => {
          setStatusNote(
            err instanceof Error ? err.message : t('common.show.jukeboxNeedCoins'),
          );
        })
        .finally(() => {
          setBusyTrackId(null);
        });
    }, 'jukebox');
  };

  if (!jukeboxEnabled) {
    return <></>;
  }

  return (
    <>
      <Button
        label={t('common.show.jukeboxButton')}
        variant="request"
        onClick={() => {
          requireAuth(() => {
            onMenuOpenChange(true);
          }, 'jukebox');
        }}
      />

      {playerEl && nowPlaying
        ? createPortal(
            <button
              type="button"
              className="jukebox-now-playing"
              onClick={() => {
                requireAuth(() => {
                  onMenuOpenChange(true);
                }, 'jukebox');
              }}
              aria-label={t('common.show.jukeboxNowPlayingScreenAria')
                .replace('{{title}}', nowPlaying.track.title)
                .replace('{{artist}}', nowPlaying.track.artist)}
            >
              <span className="jukebox-now-playing__eq" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              <span className="jukebox-now-playing__copy">
                <span className="jukebox-now-playing__label">
                  {t('common.show.jukeboxNowPlayingScreen')}
                </span>
                <span className="jukebox-now-playing__title">{nowPlaying.track.title}</span>
                <span className="jukebox-now-playing__meta">
                  {nowPlaying.track.artist}
                  {' · '}
                  {t('common.show.jukeboxRequestedBy').replace(
                    '{{name}}',
                    nowPlaying.requestedBy,
                  )}
                </span>
              </span>
              <span className="jukebox-now-playing__progress" aria-hidden="true">
                <span style={{ width: `${String(Math.round(progress * 100))}%` }} />
              </span>
            </button>,
            playerEl,
          )
        : null}

      {menuOpen && playerEl
        ? createPortal(
            <div
              className="jukebox-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="jukebox-panel-title"
            >
              <button
                type="button"
                className="jukebox-panel__backdrop"
                aria-label={t('common.show.jukeboxClose')}
                onClick={() => {
                  onMenuOpenChange(false);
                }}
              />
              <div className="jukebox-panel__shell">
                <header className="jukebox-panel__header">
                  <div>
                    <p className="jukebox-panel__eyebrow">{t('common.show.jukeboxEyebrow')}</p>
                    <h2 id="jukebox-panel-title">{t('common.show.jukeboxTitle')}</h2>
                  </div>
                  <button
                    type="button"
                    className="jukebox-panel__close"
                    onClick={() => {
                      onMenuOpenChange(false);
                    }}
                  >
                    {t('common.show.jukeboxClose')}
                  </button>
                </header>

                <p className="jukebox-panel__hint">{t('common.show.jukeboxHint')}</p>

                <section className="jukebox-panel__now" aria-label={t('common.show.jukeboxNowPlaying')}>
                  <div className="jukebox-panel__art" aria-hidden="true">
                    <span>♪</span>
                  </div>
                  <div className="jukebox-panel__now-meta">
                    <p className="jukebox-panel__kicker">{t('common.show.jukeboxNowPlaying')}</p>
                    {nowPlaying ? (
                      <>
                        <h3>{nowPlaying.track.title}</h3>
                        <p>{nowPlaying.track.artist}</p>
                        <p className="jukebox-panel__requester">
                          {t('common.show.jukeboxRequestedBy').replace(
                            '{{name}}',
                            nowPlaying.requestedBy,
                          )}
                        </p>
                        <div className="jukebox-panel__progress" aria-hidden="true">
                          <div
                            className="jukebox-panel__progress-fill"
                            style={{ width: `${String(Math.round(progress * 100))}%` }}
                          />
                        </div>
                        <div className="jukebox-panel__now-actions">
                          <span>{formatTrackDuration(nowPlaying.track.durationSec)}</span>
                        </div>
                      </>
                    ) : (
                      <p className="jukebox-panel__empty">{t('common.show.jukeboxNowPlayingEmpty')}</p>
                    )}
                  </div>
                </section>

                <div className="jukebox-panel__columns">
                  <section aria-label={t('common.show.jukeboxQueue')}>
                    <h3 className="jukebox-panel__section-title">{t('common.show.jukeboxQueue')}</h3>
                    {queue.length === 0 ? (
                      <p className="jukebox-panel__empty">{t('common.show.jukeboxQueueEmpty')}</p>
                    ) : (
                      <ol className="jukebox-panel__queue">
                        {queue.map((item, index) => (
                          <li key={item.queueId}>
                            <div>
                              <p className="jukebox-panel__track-title">
                                <span className="jukebox-panel__pos">{String(index + 1)}</span>
                                {item.track.title}
                              </p>
                              <p className="jukebox-panel__track-sub">
                                {item.track.artist} · @{item.requestedBy}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="jukebox-panel__bump"
                              disabled={
                                isTipping || busyTrackId === item.queueId || !canTip
                              }
                              onClick={() => {
                                bumpItem(item);
                              }}
                            >
                              {t('common.show.jukeboxBump').replace(
                                '{{cost}}',
                                String(item.bumpCoins),
                              )}
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                  </section>

                  <section aria-label={t('common.show.jukeboxCatalog')}>
                    <div className="jukebox-panel__catalog-head">
                      <h3 className="jukebox-panel__section-title">{t('common.show.jukeboxCatalog')}</h3>
                      <label className="jukebox-panel__search">
                        <span className="visually-hidden">{t('common.show.jukeboxSearch')}</span>
                        <input
                          value={query}
                          onChange={(event) => {
                            setQuery(event.target.value);
                          }}
                          placeholder={t('common.show.jukeboxSearch')}
                        />
                      </label>
                    </div>
                    {catalog.length === 0 ? (
                      <p className="jukebox-panel__empty">{t('common.show.jukeboxCatalogEmpty')}</p>
                    ) : (
                      <ul className="jukebox-panel__catalog">
                        {catalog.map((track) => (
                          <li key={track.id}>
                            <div>
                              <p className="jukebox-panel__track-title">{track.title}</p>
                              <p className="jukebox-panel__track-sub">
                                {track.artist} · {formatTrackDuration(track.durationSec)}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="jukebox-panel__request"
                              disabled={
                                isTipping || busyTrackId === track.id || !canTip
                              }
                              onClick={() => {
                                requestTrack(track);
                              }}
                            >
                              {t('common.show.jukeboxRequest').replace(
                                '{{cost}}',
                                String(track.requestCoins),
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </div>

                {statusNote ? <p className="jukebox-panel__status">{statusNote}</p> : null}
              </div>
            </div>,
            playerEl,
          )
        : null}
    </>
  );
}
